/**
 * Theme (dark mode) — shared constants + pure resolution logic.
 *
 * This file has NO React and NO DOM side effects beyond simple reads —
 * it's imported both by the no-flash boot script (as a source of truth
 * for the constant/logic shape, mirrored inline since the boot script
 * must be a standalone string with zero imports) and by `ThemeContext`
 * (which does the actual DOM writes). Keeping the resolution algorithm
 * here means the hook and the boot script can't silently drift apart.
 */

export type Theme = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

export const THEME_STORAGE_KEY = "buildsynx-theme";

/**
 * The standard theme for anyone who never touched the picker (owner decision,
 * 2026-09-08). Light — NOT "system" — so a phone set to Dark Mode doesn't
 * flip the app dark on its own. "System" remains an explicit choice.
 * Mirrored by hand inside THEME_BOOT_SCRIPT below; tests/theme/ pins both.
 */
export const DEFAULT_THEME: Theme = "light";

const VALID_THEMES: readonly Theme[] = ["light", "dark", "system"];

export function isValidTheme(value: unknown): value is Theme {
    return typeof value === "string" && (VALID_THEMES as readonly string[]).includes(value);
}

/** Reads the persisted theme choice. Defaults to DEFAULT_THEME ("light"). SSR-safe (no window access if unavailable). */
export function readStoredTheme(): Theme {
    if (typeof window === "undefined") return DEFAULT_THEME;
    try {
        const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
        return isValidTheme(raw) ? raw : DEFAULT_THEME;
    } catch {
        // localStorage unavailable (private mode / disabled) — fall back silently.
        return DEFAULT_THEME;
    }
}

export function persistTheme(theme: Theme): void {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
        // Ignore — private mode / storage quota. Theme still applies for this tab session.
    }
}

/** True if the OS/browser currently prefers dark. SSR-safe. */
export function systemPrefersDark(): boolean {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    try {
        return window.matchMedia("(prefers-color-scheme: dark)").matches;
    } catch {
        return false;
    }
}

/** Resolves an explicit-or-system theme choice to the actual light/dark to render. */
export function resolveTheme(theme: Theme): ResolvedTheme {
    if (theme === "system") return systemPrefersDark() ? "dark" : "light";
    return theme;
}

/** Applies the resolved theme to the document root by toggling the `dark` class. */
export function applyResolvedTheme(resolved: ResolvedTheme): void {
    if (typeof document === "undefined") return;
    document.documentElement.classList.toggle("dark", resolved === "dark");
}

/**
 * The no-flash boot script, as a source string for inline injection into the
 * root layout's <head> (see src/app/layout.tsx). Must stay dependency-free —
 * it runs before any bundle loads, before hydration, so it cannot import
 * anything from this module (or any module) at runtime. It is written to
 * mirror the logic above by hand; if you change the resolution algorithm
 * here, update this string to match.
 *
 * Kept as a template-literal-safe plain string (no backticks inside).
 */
export const THEME_BOOT_SCRIPT = `(function(){try{var k="${THEME_STORAGE_KEY}";var t=localStorage.getItem(k);if(t!=="light"&&t!=="dark"&&t!=="system")t="${DEFAULT_THEME}";var d=t==="dark"||(t==="system"&&window.matchMedia&&window.matchMedia("(prefers-color-scheme: dark)").matches);document.documentElement.classList.toggle("dark",d);}catch(e){}})();`;
